/**
 * "Release chats" (19-10-2026, Jordy): owner/admin maakt het eigenaarschap
 * ("Yours") los van alle chatters of van één chatter. De gesprekken gaan naar de
 * Waiting queue; er wordt niets verwijderd en privé-toewijzingen blijven staan.
 * Eerst het aantal, dan pas bevestigen. De server logt elke actie (audit_logs).
 * Gedeelde Modal: onder 900 px schermvullend (mobiel.css).
 */
import { useState } from 'react'
import { Modal } from './Modal'
import { api } from '../lib/api'
import { useApi } from '../lib/useApi'
import { useActions } from '../lib/actions'

interface Overzicht { totaal: number; per_admin: { admin_id: number; naam: string; aantal: number }[] }

export function ClaimsLosmaken({ onClose, onDone, start }: {
  onClose: () => void
  onDone?: () => void
  /** 19-10-2026 (Jordy): vanuit My chats meteen naar de bevestiging (alle, of één chatter). */
  start?: 'alle' | number
}) {
  const q = useApi<Overzicht>('/api/admin/conversations/panels/inbox/claims')
  const { notify } = useActions()
  const [keuze, setKeuze] = useState<number | ''>('')
  const [bevestig, setBevestig] = useState<'alle' | number | null>(start ?? null)
  const [bezig, setBezig] = useState(false)
  const totaal = q.data?.totaal ?? 0
  const per = q.data?.per_admin ?? []
  const gekozen = per.find((r) => r.admin_id === keuze)

  const losmaken = async (doel: 'alle' | number) => {
    setBezig(true)
    try {
      const r = await api.post<{ losgemaakt: number }>('/api/admin/conversations/panels/inbox/losmaken',
        doel === 'alle' ? {} : { admin_id: doel })
      notify(`Released ${r.losgemaakt} chat${r.losgemaakt === 1 ? '' : 's'} — they are in the Waiting queue now.`)
      setBevestig(null); setKeuze('')
      q.refetch(); onDone?.()
      if (start !== undefined) onClose()
    } catch (e) {
      notify(`Release failed: ${e instanceof Error ? e.message : String(e)}`, false)
    } finally {
      setBezig(false)
    }
  }

  const naam = (id: number) => per.find((r) => r.admin_id === id)?.naam ?? `#${id}`
  const aantal = (id: number) => per.find((r) => r.admin_id === id)?.aantal ?? 0

  return (
    <Modal title="Release chats" onClose={onClose}
           desc="Released chats go to the Waiting queue; any chatter can pick them up. Nothing is deleted. Private chats stay assigned.">
      {q.loading && !q.data ? <div className="mut2">Counting…</div> : null}
      {bevestig !== null && q.data ? (
        <div data-testid="losmaken-bevestig" style={{ display: 'grid', gap: 12 }}>
          <b style={{ fontSize: 15 }}>
            {bevestig === 'alle'
              ? `Release all ${totaal} chats from all chatters?`
              : `Release ${aantal(bevestig)} chats of ${naam(bevestig)}?`}
          </b>
          <div className="row" style={{ gap: 8, flexWrap: 'wrap' }}>
            <button className="btn" type="button" disabled={bezig} data-testid="losmaken-ja"
                    onClick={() => void losmaken(bevestig)}>{bezig ? 'Releasing…' : 'Yes, release'}</button>
            <button className="btn ghost" type="button" disabled={bezig}
                    onClick={() => (start !== undefined ? onClose() : setBevestig(null))}>Cancel</button>
          </div>
        </div>
      ) : q.data && bevestig === null ? (
        <div style={{ display: 'grid', gap: 16 }}>
          <div className="row" style={{ gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
            <span style={{ flex: 1, minWidth: 180 }}>All chatters: <b>{totaal}</b> chat{totaal === 1 ? '' : 's'} owned right now</span>
            <button className="btn" type="button" disabled={!totaal} data-testid="losmaken-alle"
                    onClick={() => setBevestig('alle')}>Release all chats</button>
          </div>
          <div style={{ display: 'grid', gap: 8 }}>
            <span className="mut2" style={{ fontSize: 12 }}>Release per chatter</span>
            <div className="row" style={{ gap: 8, flexWrap: 'wrap' }}>
              <select className="select" aria-label="Chatter" value={keuze} data-testid="losmaken-chatter"
                      style={{ flex: 1, minWidth: 180 }}
                      onChange={(e) => setKeuze(e.target.value ? Number(e.target.value) : '')}>
                <option value="">Choose a chatter…</option>
                {per.map((r) => <option key={r.admin_id} value={r.admin_id}>{r.naam} — {r.aantal}</option>)}
              </select>
              <button className="btn ghost" type="button" disabled={!gekozen} data-testid="losmaken-een"
                      onClick={() => gekozen && setBevestig(gekozen.admin_id)}>
                {gekozen ? `Release ${gekozen.aantal}` : 'Release'}
              </button>
            </div>
            {!per.length ? <span className="mut2" style={{ fontSize: 12 }}>No chatter owns a chat right now.</span> : null}
          </div>
        </div>
      ) : null}
    </Modal>
  )
}
